/**
 * Repository for event session invitation API calls.
 * Handles validation, fetching invitation details, and submitting registrations.
 */
export class EventSessionInvitationRepository {

	constructor() {
		let host = window.location.hostname;
		if (host === "localhost") {
			this.urlBase = 'https://dev.wepass.ai';
		} else {
			this.urlBase = window.location.origin;
		}
	}

	/**
	 * Validate an invitation token and get full details.
	 * @param {string} token - The invitation token from the URL
	 * @returns {Promise<Object>} Invitation details or error
	 */
	async validateInvitation(token) {
		const url = `${this.urlBase}/world/api/public/event-session-invitation/${token}`;

		try {
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			});

			const data = await response.json();

			if (!response.ok) {
				return {
					valid: false,
					reason: data.reason || 'Error al validar la invitación.',
				};
			}

			return data;
		} catch (error) {
			console.error('Error validating invitation:', error);
			return {
				valid: false,
				reason: 'Error de conexión. Por favor, inténtalo de nuevo.',
			};
		}
	}

	/**
	 * Submit a registration for an event invitation.
	 * @param {Object} registrationData - The registration data
	 * @param {string} registrationData.token - The invitation token
	 * @param {Object} registrationData.user - User data (name, surname, email, phone)
	 * @param {Object} registrationData.formData - Custom form field data
	 * @param {number} registrationData.companions - Number of companions
	 * @returns {Promise<Object>} Registration result
	 */
	async submitRegistration(registrationData) {
		const url = `${this.urlBase}/world/api/public/event-registration`;

		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(registrationData),
			});

			const data = await response.json();

			if (!response.ok) {
				return {
					success: false,
					error: data.error || 'Error al procesar el registro.',
				};
			}

			return data;
		} catch (error) {
			console.error('Error submitting registration:', error);
			return {
				success: false,
				error: 'Error de conexión. Por favor, inténtalo de nuevo.',
			};
		}
	}

	/**
	 * Get the redirect URL for an invitation.
	 * @param {string} token - The invitation token
	 * @returns {Promise<string|null>} The redirect URL or null on error
	 */
	async getInvitationUrl(token) {
		const url = `${this.urlBase}/world/api/public/event-session-invitation/${token}/url`;

		try {
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			});

			if (!response.ok) {
				return null;
			}

			const data = await response.json();
			return data.url;
		} catch (error) {
			console.error('Error getting invitation URL:', error);
			return null;
		}
	}

	/**
	 * Get all event registrations for a user.
	 * @param {string} hostCode - The user's hostCode
	 * @returns {Promise<Object>} Object with registrations array
	 */
	async getUserEventRegistrations(hostCode) {
		const url = `${this.urlBase}/world/api/public/user/${hostCode}/event-registrations`;

		try {
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			});

			if (!response.ok) {
				return {
					result: -1,
					registrations: [],
				};
			}

			const data = await response.json();
			return data;
		} catch (error) {
			console.error('Error getting user event registrations:', error);
			return {
				result: -1,
				registrations: [],
			};
		}
	}
}

export const eventSessionInvitationRepository = new EventSessionInvitationRepository();

